import { ChainId } from '@src/configs/chainInfo'
import { approximateGasFeesEvm } from '@src/hooks/evm/approximateGasFees'
import { approximateGasFeesSolana } from '@src/hooks/solana/approximateGasFees'
import { approximateGasFeesTron } from '@src/hooks/tron/approximateGasFees'
import { IExchangeData } from '@src/types/exchange'
import { GasFeeSimulationResults } from '@src/types/gasFees'
import { PrecomputeGasResults, TransactionType } from '@src/types/gasPrices'
import { gasFeeslogger } from '@src/utils/logger'

const PRINT_GAS_ERRORS = true

export const approximateGasFees = (
    precomputedGasPrices: PrecomputeGasResults,
    sellData: IExchangeData,
    isCCTP: boolean,
    isNative: boolean,
    isApprovalNeeded: boolean,
    isWrapOrUnwrapOnly: boolean
): GasFeeSimulationResults => {
    try {
        if (!precomputedGasPrices[sellData.chainId]) {
            throw new Error(
                `[approximateGasFees] precomputedGasPrices does not contain data for chainId ${sellData.chainId}`
            )
        }

        const precomputed_results: GasFeeSimulationResults[] = []

        // Add 3 wraps to leave gas for a possible unwrap
        if (isWrapOrUnwrapOnly) {
            precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.WRAP])
        } else {
            if (isCCTP) {
                if (isApprovalNeeded) {
                    precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE])
                }
                precomputed_results.push(
                    precomputedGasPrices[sellData.chainId].results[TransactionType.DEPOSIT_FOR_BURN]
                )
            } else {
                if (isNative) {
                    // Base operations needed for native swaps
                    precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.WRAP])
                    precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE])
                    precomputed_results.push(
                        precomputedGasPrices[sellData.chainId].results[TransactionType.PLACE_ORDER]
                    )

                    // Add operations 3 more times for a larger buffer (total 4x)
                    for (let i = 0; i < 3; i++) {
                        precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.WRAP])
                        precomputed_results.push(
                            precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE]
                        )
                        precomputed_results.push(
                            precomputedGasPrices[sellData.chainId].results[TransactionType.PLACE_ORDER]
                        )
                    }
                } else {
                    if (isApprovalNeeded) {
                        precomputed_results.push(
                            precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE]
                        )
                    }
                    precomputed_results.push(
                        precomputedGasPrices[sellData.chainId].results[TransactionType.PLACE_ORDER]
                    )
                }
            }

            gasFeeslogger.debug('Precomputed results:', { precomputed_results })
        }

        switch (sellData.chainId) {
            case ChainId.Solana:
                return approximateGasFeesSolana(precomputedGasPrices, precomputed_results)
            case ChainId.Tron:
                return approximateGasFeesTron(precomputedGasPrices, precomputed_results)
            default:
                return approximateGasFeesEvm(
                    precomputedGasPrices,
                    precomputed_results,
                    sellData,
                    isCCTP,
                    isNative,
                    isApprovalNeeded,
                    isWrapOrUnwrapOnly
                )
        }
    } catch (err) {
        if (PRINT_GAS_ERRORS) {
            gasFeeslogger.error('Cannot approximate gas. Params:', {
                sellData: sellData,
                precomputedGasPrices: precomputedGasPrices,
                isNative: isNative,
                isCCTP: isCCTP,
                isApprovalNeeded: isApprovalNeeded,
            })
            gasFeeslogger.error('Error', { error: err })
        }

        return {
            totalGasUnits: BigInt(0),
            indivdualGasUnits: [BigInt(0)],
            totalFeePerGasUnit: BigInt(0),
            feeMetrics: {},
        } as GasFeeSimulationResults
    }
}
